// @ts-check
/**
 * Typed client for the Library API (modules, checks and check runs).
 *
 * TYPES ARE GENERATED, NOT HAND-WRITTEN. See `schema.d.ts` header for details.
 * A module is addressed by its path (e.g. `services/newcobackend/api_keys`).
 * Every function returns the response body.
 */
import axios from 'axios'

/** @typedef {import('./schema').components['schemas']} Schemas */

/** @typedef {Schemas['LibraryNodeList']} ModuleRow */
/** @typedef {Schemas['LibraryNodeDetail']} ModuleDetail */
/** @typedef {Schemas['LibraryNodeWrite']} ModuleWrite */
/** @typedef {Schemas['PatchedLibraryNodeWrite']} ModulePatch */
/** @typedef {Schemas['LibraryNodeHistoryRequest']} ModuleHistoryRequest */
/** @typedef {Schemas['LibraryEdgeRequest']} EdgeRequest */
/** @typedef {Schemas['LibraryTest']} Check */
/** @typedef {Schemas['PatchedLibraryTest']} CheckPatch */
/** @typedef {Schemas['LibraryTestHistoryEntry']} CheckHistoryEntry */
/** @typedef {Schemas['LibraryOwner']} Owner */
/** @typedef {Schemas['LibraryTestRunResponse']} RunStarted */
/** @typedef {Schemas['LibraryCheckRun']} CheckRun */

const MODULES = '/api/library-nodes/'
const CHECKS = '/api/library-tests/'
const RUNS = '/api/library-check-runs/'

/** @param {string} path @param {string} [sub] */
const moduleUrl = (path, sub = '') => `${MODULES}${path.replace(/^\/+|\/+$/g, '')}/${sub}`

/**
 * GET /api/library-nodes/
 * @returns {Promise<ModuleRow[]>}
 */
export async function listModules() {
  const { data } = await axios.get(MODULES)
  return data
}

/**
 * GET /api/library-nodes/{path}/
 * @param {string} path
 * @returns {Promise<ModuleDetail>}
 */
export async function getModule(path) {
  const { data } = await axios.get(moduleUrl(path))
  return data
}

/**
 * POST /api/library-nodes/ — the slug comes from the title.
 * @param {Partial<ModuleWrite> & Record<string, unknown>} body
 * @returns {Promise<ModuleWrite>} includes the new `path`
 */
export async function createModule(body) {
  const { data } = await axios.post(MODULES, body)
  return data
}

/**
 * PATCH /api/library-nodes/{path}/ — edit fields, move (`parent_path`) or
 * rename (`slug`). A body change needs a `note`.
 * @param {string} path
 * @param {ModulePatch & Record<string, unknown>} fields
 * @returns {Promise<ModuleWrite>} includes the (possibly new) `path`
 */
export async function updateModule(path, fields) {
  const { data } = await axios.patch(moduleUrl(path), fields)
  return data
}

/**
 * DELETE /api/library-nodes/{path}/
 * @param {string} path
 * @returns {Promise<void>}
 */
export async function deleteModule(path) {
  await axios.delete(moduleUrl(path))
}

/**
 * POST /api/library-nodes/{path}/history/
 * @param {string} path
 * @param {ModuleHistoryRequest} body
 * @returns {Promise<ModuleDetail>}
 */
export async function addModuleHistory(path, body) {
  const { data } = await axios.post(moduleUrl(path, 'history/'), body)
  return data
}

/**
 * POST /api/library-nodes/{path}/edges/
 * @param {string} path
 * @param {EdgeRequest} edge
 * @returns {Promise<ModuleDetail>}
 */
export async function addModuleEdge(path, edge) {
  const { data } = await axios.post(moduleUrl(path, 'edges/'), edge)
  return data
}

/**
 * DELETE /api/library-nodes/{path}/edges/ — same body as addModuleEdge
 * (the schema omits DELETE bodies).
 * @param {string} path
 * @param {EdgeRequest} edge
 * @returns {Promise<ModuleDetail>}
 */
export async function removeModuleEdge(path, edge) {
  const { data } = await axios.delete(moduleUrl(path, 'edges/'), { data: edge })
  return data
}

/**
 * GET /api/library-tests/
 * @returns {Promise<Check[]>}
 */
export async function listChecks() {
  const { data } = await axios.get(CHECKS)
  return data
}

/**
 * POST /api/library-tests/ — `phrase` and `module_path` are required; dependencies go
 * in `needs_ids` (existing checks) and `needs_phrases` (sentences). `module` and
 * `needs` are read-only in responses.
 * @param {Pick<Check, 'phrase'> & Partial<Pick<Check, 'module_path' | 'owner' | 'status' | 'needs_ids' | 'needs_phrases'>>} body
 * @returns {Promise<Check>}
 */
export async function createCheck(body) {
  const { data } = await axios.post(CHECKS, body)
  return data
}

/**
 * PATCH /api/library-tests/{id}/ — sending `needs_ids`/`needs_phrases` replaces the dependencies.
 * @param {number} id
 * @param {Pick<CheckPatch, 'phrase' | 'owner' | 'status' | 'needs_ids' | 'needs_phrases'>} body
 * @returns {Promise<Check>}
 */
export async function updateCheck(id, body) {
  const { data } = await axios.patch(`${CHECKS}${id}/`, body)
  return data
}

/**
 * DELETE /api/library-tests/{id}/
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function deleteCheck(id) {
  await axios.delete(`${CHECKS}${id}/`)
}

/**
 * GET /api/library-tests/{id}/history/
 * @param {number} id
 * @returns {Promise<CheckHistoryEntry[]>}
 */
export async function getCheckHistory(id) {
  const { data } = await axios.get(`${CHECKS}${id}/history/`)
  return data
}

/**
 * @typedef {{ tenants: { id: number, name: string, slug: string | null }[],
 *   features: { feature_id: string, name: string, category: string }[] }} ScopeOptions
 */

/**
 * GET /api/library-nodes/scope-options/ — every tenant and feature, for the
 * "Visible to" / "Only if the customer has" pickers.
 * @returns {Promise<ScopeOptions>}
 */
export async function getScopeOptions() {
  const { data } = await axios.get(`${MODULES}scope-options/`)
  return data
}

/**
 * GET /api/library-tests/owners/
 * @returns {Promise<Owner[]>}
 */
export async function listCheckOwners() {
  const { data } = await axios.get(`${CHECKS}owners/`)
  return data
}

/**
 * POST /api/library-tests/run/ — start the checker for these check ids.
 * @param {number[]} ids
 * @returns {Promise<RunStarted>}
 */
export async function runChecks(ids) {
  const { data } = await axios.post(`${CHECKS}run/`, { ids })
  return data
}

/**
 * GET /api/library-check-runs/
 * @returns {Promise<CheckRun[]>}
 */
export async function listCheckRuns() {
  const { data } = await axios.get(RUNS)
  return data
}
